import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import routing from "routing";
import { SEARCH_PATH_NAMESPACE } from "constants/i18n-namespaces";
import {
  getBiller,
  getSelectedPayableType,
  getPayableSession,
  getLabels,
  getIsMobile,
} from "selectors";
import { setPayableSession } from "actions/payableSessionActions";
import { ACTION } from "actions/paymentIntentActions";
import { getLabel } from "utilities/helper";
import { Breadcrumb, BreadcrumbItem } from "../breadcrumb";

type CurrentStep =
  | "select-item"
  | "lookup"
  | "invoice-list"
  | "details"
  | "payment"
  | "review";

export interface PayableBreadcrumbProps {
  className?: string;
}

export const PayableBreadcrumb: React.FC<PayableBreadcrumbProps> = ({
  className,
}) => {
  const { t } = useTranslation(SEARCH_PATH_NAMESPACE);
  const dispatch = useDispatch();
  const location = useLocation();

  const isMobile = useSelector(getIsMobile);

  const payableSession = useSelector(getPayableSession);
  const payableTypeFromStore = useSelector(getSelectedPayableType);

  const labelSource = useSelector(getLabels);
  const biller = useSelector(getBiller);

  if (!biller) {
    return null;
  }

  const {
    payableType: payableTypeFromSession,
    payable,
    lookupResults,
  } = payableSession || {};

  const payableType = payableTypeFromSession ?? payableTypeFromStore;

  const getCurrentStepFromUrl = (): CurrentStep => {
    const pathname = location.pathname;

    if (pathname === routing.index()) {
      return "select-item";
    } else if (pathname === routing.lookup.index()) {
      return "lookup";
    } else if (pathname === routing.invoiceList.index()) {
      return "invoice-list";
    } else if (pathname === routing.payableDetails.index()) {
      return "details";
    } else if (pathname === routing.payableDashboard.index()) {
      return "payment";
    } else if (pathname === routing.payableReview.index()) {
      return "review";
    } else {
      return "select-item";
    }
  };

  const currentStep = getCurrentStepFromUrl();

  const shouldShowSearch = () => {
    return (
      biller.payableTypeDefinitions && biller.payableTypeDefinitions.length > 1
    );
  };

  const shouldShowInvoiceList = () => {
    return (
      payableType?.category.toLowerCase() === "invoice" &&
      lookupResults &&
      lookupResults.length > 1
    );
  };

  const shouldShowDetails = () => {
    if (!payable || !payableType) return false;

    const category = payable.category.toLowerCase();
    const hasExtensionFields =
      payableType.extensionFields && payableType.extensionFields.length > 0;

    return (
      (category === "invoice" ||
        category === "order" ||
        category === "freeform") &&
      hasExtensionFields
    );
  };

  const getPayableTypeLabel = () => {
    if (!payableType) return "";
    return getLabel(labelSource.labels, payableType.name) || payableType.name;
  };

  const handleSearchClick = () => {
    dispatch(setPayableSession(undefined));
  };

  const handleLookupClick = () => {
    if (shouldShowInvoiceList()) {
      dispatch({ type: ACTION.RESET_PAYMENT_INTENT_STATE });
    } else {
      dispatch(setPayableSession(undefined));
    }
  };

  const handlePaymentClick = () => {
    dispatch({ type: ACTION.RESET_PAYMENT_INTENT_STATE });
  };

  const buildBreadcrumbItems = (): BreadcrumbItem[] => {
    const items: BreadcrumbItem[] = [];
    const stepOrder: CurrentStep[] = [
      "select-item",
      "lookup",
      "invoice-list",
      "details",
      "payment",
      "review",
    ];

    const currentStepIndex = stepOrder.indexOf(currentStep);

    // Search step.
    if (shouldShowSearch() && currentStepIndex >= 0) {
      items.push({
        label: t("select-item"),
        to: currentStep !== "select-item" ? routing.index() : undefined,
        onClick: currentStep !== "select-item" ? handleSearchClick : undefined,
        isActive: currentStep === "select-item",
      });
    }

    // Lookup step.
    if (currentStepIndex >= stepOrder.indexOf("lookup")) {
      items.push({
        label:
          payableType.category.toLowerCase() === "account"
            ? t("locate")
            : t("search"),
        to: currentStep !== "lookup" ? routing.lookup.index() : undefined,
        onClick: currentStep !== "lookup" ? handleLookupClick : undefined,
        isActive: currentStep === "lookup",
      });
    }

    // Invoice list step.
    if (
      shouldShowInvoiceList() &&
      currentStepIndex >= stepOrder.indexOf("invoice-list")
    ) {
      items.push({
        label: getPayableTypeLabel(),
        to:
          currentStep !== "invoice-list"
            ? routing.invoiceList.index()
            : undefined,
        onClick: undefined,
        isActive: currentStep === "invoice-list",
      });
    }

    // Details step.
    if (
      shouldShowDetails() &&
      currentStepIndex >= stepOrder.indexOf("details")
    ) {
      items.push({
        label: t("payable-details"),
        to:
          currentStep !== "details"
            ? routing.payableDetails.index()
            : undefined,
        onClick: undefined,
        isActive: currentStep === "details",
      });
    }

    // Payment step.
    if (currentStepIndex >= stepOrder.indexOf("payment")) {
      items.push({
        label: t("payable-dashboard"),
        to:
          currentStep !== "payment"
            ? routing.payableDashboard.index()
            : undefined,
        onClick: currentStep !== "payment" ? handlePaymentClick : undefined,
        isActive: currentStep === "payment",
      });
    }

    // Review step.
    if (currentStepIndex >= stepOrder.indexOf("review")) {
      items.push({
        label: t("review"),
        to: undefined, // Can't navigate back to review once submitted
        onClick: undefined,
        isActive: currentStep === "review",
      });
    }

    return items;
  };

  const breadcrumbItems = buildBreadcrumbItems();

  return (
    <Breadcrumb
      items={breadcrumbItems}
      className={className}
      isMobile={isMobile}
    />
  );
};
